import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useSearchParams } from 'wouter';
import { api } from '../lib/api';
import { applicationLabels, money, shortDate, todayInput } from '../lib/format';
import { fieldErrors } from '../lib/form-errors';
import { parseTagInput, tagInputError } from '../lib/order-tags';
import { haptic } from '../lib/telegram';
import type { Application, ApplicationStatus, ListResponse, Order } from '../types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  LoadingScreen,
  Modal,
  PageHeader,
  Select,
  StatusBadge,
} from '../components/ui';

/** Русские названия полей формы приёма: по ним ошибка находит своё место. */
const ACCEPT_FIELD_LABELS: Record<string, string> = {
  totalPrice: 'Итоговая стоимость',
  depositRequired: 'Предоплата',
  deadline: 'Дедлайн',
  status: 'Стартовый статус',
  tags: 'Теги',
};

const tabs: { value: ApplicationStatus; label: string }[] = [
  { value: 'NEW', label: 'Новые' },
  { value: 'NEEDS_INFO', label: 'Уточнение' },
  { value: 'ACCEPTED', label: 'Принятые' },
  { value: 'REJECTED', label: 'Отклонённые' },
];

type ApplicationAction = 'details' | 'accept' | 'reject' | 'info';

function isApplicationTab(value: string | null): value is ApplicationStatus {
  return tabs.some((tab) => tab.value === value);
}

export function ApplicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStatus = searchParams.get('status');
  const status: ApplicationStatus = isApplicationTab(requestedStatus) ? requestedStatus : 'NEW';
  const [selected, setSelected] = useState<{
    application: Application;
    action: ApplicationAction;
  } | null>(null);
  const query = useQuery({
    queryKey: ['applications', status],
    queryFn: () => api<ListResponse<Application>>(`/applications?status=${status}&limit=100`),
  });

  return (
    <div className="applications-page">
      <PageHeader eyebrow="Входящие" title="Заявки" />
      <div className="tabs tabs--scroll">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            className={status === tab.value ? 'is-active' : ''}
            onClick={() => setSearchParams({ status: tab.value }, { replace: true })}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {query.isPending && <LoadingScreen label="Проверяем входящие" />}
      {query.error && <ErrorNotice error={query.error} />}
      {query.data?.items.length === 0 && (
        <EmptyState
          title="Здесь пока пусто"
          text="Как только кто-то заполнит форму на вашей странице, заявка придёт сюда — и в Telegram."
        />
      )}
      <div className="list">
        {query.data?.items.map((application) => (
          <Card key={application.id} className="application-card">
            <div
              className="application-card__open"
              role="button"
              tabIndex={0}
              onClick={() => setSelected({ application, action: 'details' })}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelected({ application, action: 'details' });
                }
              }}
            >
              <div className="application-card__person">
                <span className="application-card__avatar" aria-hidden="true">
                  {(application.client?.displayName ?? '?').slice(0, 1).toUpperCase()}
                </span>
                <span className="application-card__identity">
                  <strong>{application.client?.displayName ?? 'Неизвестный клиент'}</strong>
                  <small>
                    {application.publicNumber} · {shortDate(application.submittedAt)}
                  </small>
                </span>
                <StatusBadge
                  tone={
                    application.status === 'NEW'
                      ? 'accent'
                      : application.status === 'NEEDS_INFO'
                        ? 'warning'
                        : 'neutral'
                  }
                >
                  {applicationLabels[application.status]}
                </StatusBadge>
              </div>

              <div className="application-card__brief">
                <h2>{application.title}</h2>
                <p>{application.description}</p>
              </div>

              <div className="application-card__facts">
                <span>
                  {application.service?.title ?? 'Без услуги'} ·{' '}
                  {shortDate(application.desiredDeadline)}
                </span>
                <strong className="numeric">
                  {application.budget
                    ? money(application.budget, application.currency)
                    : 'Бюджет не указан'}
                </strong>
              </div>
            </div>

            {(application.status === 'NEW' || application.status === 'NEEDS_INFO') && (
              <div className="application-card__actions">
                <Button onClick={() => setSelected({ application, action: 'accept' })}>
                  Принять
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setSelected({ application, action: 'info' })}
                >
                  Уточнить
                </Button>
                <Button
                  variant="ghost"
                  aria-label="Отклонить заявку"
                  onClick={() => setSelected({ application, action: 'reject' })}
                >
                  ×
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>
      {selected && (
        <ApplicationSheet
          application={selected.application}
          initialAction={selected.action}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function ApplicationSheet({
  application,
  initialAction,
  onClose,
}: {
  application: Application;
  initialAction: ApplicationAction;
  onClose: () => void;
}) {
  const [action, setAction] = useState<ApplicationAction>(initialAction);
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [price, setPrice] = useState(application.budget ?? '');
  const [deposit, setDeposit] = useState('');
  const [deadline, setDeadline] = useState(application.desiredDeadline ?? '');
  const [startStatus, setStartStatus] = useState<'WAITING_PAYMENT' | 'QUEUED'>('WAITING_PAYMENT');
  const [message, setMessage] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [installments, setInstallments] = useState(false);
  const [installmentParts, setInstallmentParts] = useState('2');
  const parsedTags = parseTagInput(tagInput);
  const tagsError = tagInputError(tagInput);
  const partsValid =
    /^\d+$/.test(installmentParts) &&
    Number(installmentParts) >= 2 &&
    Number(installmentParts) <= 12;

  const accept = useMutation({
    mutationFn: () =>
      api<Order>(`/applications/${application.id}/accept`, {
        method: 'POST',
        body: {
          totalPrice: price,
          ...(deposit ? { depositRequired: deposit } : {}),
          ...(deadline ? { deadline } : {}),
          tags: parsedTags,
          // Рассрочка включается у заказа: по ней бот следит за остатком.
          installmentParts: installments ? Number(installmentParts) : null,
          status: startStatus,
          notifyClient: true,
        },
      }),
    onSuccess: (order) => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      navigate(`/orders/${order.id}`);
    },
  });
  const acceptErrors = fieldErrors(accept.error);
  const decision = useMutation({
    mutationFn: (kind: 'reject' | 'request-info') =>
      api<Application>(`/applications/${application.id}/${kind}`, {
        method: 'POST',
        body: { message: message || null, notifyClient: true },
      }),
    onSuccess: () => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      onClose();
    },
  });

  return (
    <Modal
      title={
        action === 'accept'
          ? 'Принять заявку'
          : action === 'reject'
            ? 'Отклонить заявку'
            : action === 'info'
              ? 'Запросить уточнение'
              : application.title
      }
      onClose={onClose}
    >
      {action === 'details' && (
        <div className="stack">
          <div className="detail-hero">
            <StatusBadge tone="accent">{applicationLabels[application.status]}</StatusBadge>
            <p className="meta">
              {application.publicNumber} · {shortDate(application.submittedAt)}
            </p>
          </div>
          <div className="detail-grid">
            <div>
              <span>Клиент</span>
              <strong>{application.client?.displayName ?? '—'}</strong>
            </div>
            <div>
              <span>Услуга</span>
              <strong>{application.service?.title ?? '—'}</strong>
            </div>
            <div>
              <span>Бюджет</span>
              <strong>
                {application.budget ? money(application.budget, application.currency) : '—'}
              </strong>
            </div>
            <div>
              <span>Желаемый срок</span>
              <strong>{shortDate(application.desiredDeadline)}</strong>
            </div>
          </div>
          <div className="prose-block">
            <span>Описание</span>
            <p>{application.description}</p>
          </div>
          {application.referenceLinks.length > 0 && (
            <div className="prose-block">
              <span>Референсы</span>
              {application.referenceLinks.map((link) => (
                <a key={link} href={link} target="_blank" rel="noreferrer">
                  {link}
                </a>
              ))}
            </div>
          )}
          {(application.status === 'NEW' || application.status === 'NEEDS_INFO') && (
            <div className="action-row action-row--wrap">
              <Button onClick={() => setAction('accept')}>Принять</Button>
              <Button variant="secondary" onClick={() => setAction('info')}>
                Уточнить
              </Button>
              <Button variant="ghost" onClick={() => setAction('reject')}>
                Отклонить
              </Button>
            </div>
          )}
        </div>
      )}

      {action === 'accept' && (
        <form
          // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
          noValidate
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            accept.mutate();
          }}
        >
          <Field label="Итоговая стоимость" error={acceptErrors.totalPrice}>
            <Input
              required
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              placeholder="5000"
            />
          </Field>
          <Field
            label="Предоплата"
            hint="Пусто — посчитаем по проценту из профиля"
            error={acceptErrors.depositRequired}
          >
            <Input
              inputMode="decimal"
              value={deposit}
              onChange={(event) => setDeposit(event.target.value)}
              placeholder="2500"
            />
          </Field>
          <Field label="Дедлайн" error={acceptErrors.deadline}>
            <Input
              type="date"
              min={todayInput()}
              value={deadline}
              onChange={(event) => setDeadline(event.target.value)}
            />
          </Field>
          <Field
            label="Теги заказа"
            hint={`${parsedTags.length}/8 · через запятую`}
            error={tagsError ?? acceptErrors.tags}
          >
            <Input
              value={tagInput}
              onChange={(event) => setTagInput(event.target.value)}
              placeholder="портрет, фэнтези, чиби"
            />
          </Field>
          <label className="toggle-row">
            <span>Рассрочка</span>
            <button
              type="button"
              role="switch"
              aria-checked={installments}
              aria-label="Рассрочка"
              className={`switch ${installments ? 'is-on' : ''}`}
              onClick={() => setInstallments(!installments)}
            >
              <span />
            </button>
          </label>
          {installments && (
            <Field
              label="Частей оплаты"
              hint="Напомним об остатке, пока рассрочка не закрыта"
              error={partsValid ? acceptErrors.installmentParts : 'От 2 до 12'}
            >
              <Input
                type="number"
                min={2}
                max={12}
                value={installmentParts}
                onChange={(event) => setInstallmentParts(event.target.value)}
              />
            </Field>
          )}
          <Field label="Стартовый статус" error={acceptErrors.status}>
            <Select
              value={startStatus}
              onChange={(event) =>
                setStartStatus(event.target.value as 'WAITING_PAYMENT' | 'QUEUED')
              }
            >
              <option value="WAITING_PAYMENT">Ожидается оплата</option>
              <option value="QUEUED">В очереди</option>
            </Select>
          </Field>
          {accept.error && <ErrorNotice error={accept.error} labels={ACCEPT_FIELD_LABELS} />}
          <div className="action-row">
            <Button type="button" variant="secondary" onClick={() => setAction('details')}>
              Назад
            </Button>
            <Button
              type="submit"
              disabled={
                !price || Boolean(tagsError) || (installments && !partsValid) || accept.isPending
              }
            >
              {accept.isPending ? 'Создаём…' : 'Создать заказ'}
            </Button>
          </div>
        </form>
      )}

      {(action === 'reject' || action === 'info') && (
        <form
          // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
          noValidate
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            decision.mutate(action === 'reject' ? 'reject' : 'request-info');
          }}
        >
          <p className="muted">
            Сообщение будет отправлено клиенту через Telegram, если заявка привязана к аккаунту.
          </p>
          <Field label={action === 'reject' ? 'Причина' : 'Что нужно уточнить'}>
            <textarea
              className="input input--textarea"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Напишите короткое сообщение"
            />
          </Field>
          {decision.error && <ErrorNotice error={decision.error} />}
          <div className="action-row">
            <Button type="button" variant="secondary" onClick={() => setAction('details')}>
              Назад
            </Button>
            <Button
              type="submit"
              variant={action === 'reject' ? 'danger' : 'primary'}
              disabled={decision.isPending}
            >
              {action === 'reject' ? 'Отклонить' : 'Отправить запрос'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
